import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { QueryProvider } from '@/lib/api/query-client'
import { ViewProvider } from '@/contexts/ViewContext'
import { PublishSuccessProvider } from '@/contexts/PublishSuccessContext'
import { CustomToaster } from '@/lib/CustomToaster'
import AuthGuard from '@/components/auth/AuthGuard'
import Login from '@/components/auth/Login'
import ConfirmLogin from '@/components/auth/ConfirmLogin'
import ForgotPassword from '@/components/auth/ForgotPassword'
import ResetPasswordCode from '@/components/auth/ResetPasswordCode'
import NewPassword from '@/components/auth/NewPassword'
import AccountSetup from '@/components/auth/AccountSetup'
import AdminLayout from '@/components/admin/AdminLayout'
import Welcome from '@/pages/dashboard/WelcomePage'
import AdminDashboard from '@/pages/dashboard'
import AdminUsers from '@/pages/users'
import AdminSettings from '@/pages/settings'
import Content from '@/pages/content'
import PressRelease from '@/pages/articles-pages/press-release'
import PressReleaseDetailPage from '@/pages/articles-pages/press-release/PressReleaseDetailPage'
import PressReleaseContentView from '@/pages/articles-pages/press-release/PressReleaseContentView'
import PressReleaseEditor from '@/pages/articles-pages/press-release/PressReleaseEditor'
import StrategicPillars from '@/pages/articles-pages/strategic-pillars'
import StrategicPillarDetailPage from '@/pages/articles-pages/strategic-pillars/StrategicPillarDetailPage'
import StrategicPillarContentView from '@/pages/articles-pages/strategic-pillars/StrategicPillarContentView'
import StrategicPillarEditor from '@/pages/articles-pages/strategic-pillars/StrategicPillarEditor'
import Programmes from '@/pages/articles-pages/programmes'
import ProgrammeDetailPage from '@/pages/articles-pages/programmes/ProgrammeDetailPage'
import ProgrammeContentView from '@/pages/articles-pages/programmes/ProgrammeContentView'
// import ProgrammeEditor from '@/pages/articles-pages/programmes/ProgrammeEditor'
import CreateAndEditProgramme from '@/pages/articles-pages/programmes/CreateAndEditProgramme'
import Publications from '@/pages/resources/publications'
import PublicationDetailPage from '@/pages/resources/publications/PublicationDetailPage'
import PublicationEditor from '@/pages/resources/publications/PublicationEditor'
import LegalFoundations from '@/pages/resources/legal-foundations'
import LegalFoundationDetailPage from '@/pages/resources/legal-foundations/LegalFoundationDetailPage'
import LegalFoundationEditor from '@/pages/resources/legal-foundations/LegalFoundationEditor'
import PhotoGalleryFolders from '@/pages/multimedia/photo-gallery'
import PhotoGalleryList from '@/pages/multimedia/photo-gallery/PhotoGalleryList'
import PhotoUploader from '@/pages/multimedia/photo-gallery/PhotoUploader'
import VideoFolders from '@/pages/multimedia/videos'
import VideoList from '@/pages/multimedia/videos/VideoList'
import Subscribers from '@/pages/subscribers'
import AdminUsersPage from '@/pages/admin-users'
import UserDetails from '@/pages/admin-users/UserDetails'
import MemberCountries from '@/pages/about/member-countries'
import MemberCountryRegion from '@/pages/about/member-countries/MemberCountryRegion'
import MemberCountryDetailPage from '@/pages/about/member-countries/MemberCountryDetailPage'
import MemberCountryEditor from '@/pages/about/member-countries/MemberCountryEditor'
import Members from '@/pages/about/leadership'
import MemberEditor from '@/pages/about/leadership/MemberEditor'
import MemberDetailPage from '@/pages/about/leadership/MemberDetailPage'
import WebPages from '@/pages/content/web-pages'
import WebPageVersions from '@/pages/content/web-pages/WebPageVersions'
import { ModalProvider } from "@/contexts/ModalContext";
import History from '@/pages/about/history'
import HistoryDetailPage from '@/pages/about/history/HistoryDetailPage'
import HistoryEditor from '@/pages/about/history/HistoryEditor'
import Documents from '@/pages/opportunities/procurement-docs'
import DocumentDetailPage from '@/pages/opportunities/procurement-docs/DocumentDetailPage'
import DocumentEditor from '@/pages/opportunities/procurement-docs/DocumentEditor'
import Jobs from '@/pages/opportunities/jobs'
import JobDetailPage from '@/pages/opportunities/jobs/JobDetailPage'
import JobEditor from '@/pages/opportunities/jobs/JobEditor'
import Applications from '@/pages/opportunities/applications'
import ApplicationDetailPage from '@/pages/opportunities/applications/ApplicationDetailPage'
import ApplicationEditor from '@/pages/opportunities/applications/ApplicationEditor'
import Tenders from '@/pages/opportunities/tenders'
import TenderDetailPage from '@/pages/opportunities/tenders/TenderDetailPage'
import TenderEditor from '@/pages/opportunities/tenders/TenderEditor'
import Events from '@/pages/events'
import EventDetailPage from '@/pages/events/EventDetailPage'
import EventEditor from '@/pages/events/EventEditorPage'
import Announcements from '@/pages/announcement'
import MyProfile from '@/pages/my-profile'

// Initialize API services
import '@/lib/api/services'
import { MicroSiteProvider } from '@/contexts/MicroSiteContext'

const AdminApp = () => {
  return (
    <>
      {/* Mobile / tablet gate – visible only below lg breakpoint */}
      <div className="flex lg:hidden min-h-screen flex-col items-center justify-center gap-6 bg-white px-8 text-center">
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[#F8E8EB]">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-[#8F2F44]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="3" width="20" height="14" rx="2" />
            <path d="M8 21h8M12 17v4" />
          </svg>
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Desktop Required</h1>
          <p className="mt-2 text-gray-500 max-w-xs">
            The AFSA Admin Dashboard is optimised for desktop use. Please open this page on a laptop or desktop computer for the best experience.
          </p>
        </div>
      </div>

      {/* Full app – visible only at lg and above */}
      <div className="hidden lg:block">
        <QueryProvider>
          <CustomToaster />
          <ModalProvider>
            <Router>
              <PublishSuccessProvider>
                <MicroSiteProvider>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/confirm-login" element={<ConfirmLogin />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password/code" element={<ResetPasswordCode />} />
                <Route path="/new-password" element={<NewPassword />} />
                <Route path="/account-setup" element={<AccountSetup />} />

                <Route
                  path="/admin"
                  element={
                    <AuthGuard requiredRole="admin_staff">
                      <Welcome />
                    </AuthGuard>
                  }
                />

                <Route
                  path="/admin/*"
                  element={
                    <AuthGuard requiredRole="admin_staff">
                      <ViewProvider>
                        <AdminLayout>
                          <Routes>
                            <Route path="/dashboard" element={<AdminDashboard />} />
                            <Route path="/users" element={<AdminUsers />} />
                            <Route path="/content">
                              <Route index element={<Content />} />
                              <Route path="web-page/:id">
                                <Route index element={<WebPageVersions />} />
                                <Route path="edit/:contentId" element={<WebPages />} />
                              </Route>
                            </Route>
                            <Route path="/settings" element={<AdminSettings />} />
                            <Route path="/press-release">
                              <Route index element={<PressRelease />} />
                              <Route path="new" element={<PressReleaseEditor />} />
                              <Route path=":id" element={<PressReleaseDetailPage />} />
                              <Route path=":id/content" element={<PressReleaseContentView />} />
                              <Route path=":id/edit" element={<PressReleaseEditor />} />
                            </Route>
                            <Route path="/strategic-pillars">
                              <Route index element={<StrategicPillars />} />
                              <Route path="new" element={<StrategicPillarEditor />} />
                              <Route path=":id" element={<StrategicPillarDetailPage />} />
                              <Route path=":id/content" element={<StrategicPillarContentView />} />
                              <Route path=":id/edit" element={<StrategicPillarEditor />} />
                            </Route>
                            <Route path="/programmes">
                              <Route index element={<Programmes />} />
                              <Route path="new" element={<CreateAndEditProgramme />} />
                              <Route path=":id" element={<ProgrammeDetailPage />} />
                              <Route path=":id/content" element={<ProgrammeContentView />} />
                              <Route path=":id/edit" element={<CreateAndEditProgramme />} />
                            </Route>
                            <Route path="/publications">
                              <Route index element={<Publications />} />
                              <Route path="new" element={<PublicationEditor />} />
                              <Route path=":id" element={<PublicationDetailPage />} />
                              <Route path=":id/edit" element={<PublicationEditor />} />
                            </Route>
                            <Route path="/legal-foundations">
                              <Route index element={<LegalFoundations />} />
                              <Route path="new" element={<LegalFoundationEditor />} />
                              <Route path=":id" element={<LegalFoundationDetailPage />} />
                              <Route path=":id/edit" element={<LegalFoundationEditor />} />
                            </Route>
                            <Route path="/photo-gallery">
                              <Route index element={<PhotoGalleryFolders />} />
                              <Route path=":folderId" element={<PhotoGalleryList />} />
                              <Route path=":folderId/upload" element={<PhotoUploader />} />
                              <Route path=":folderId/edit/:galleryId" element={<PhotoUploader />} />
                            </Route>
                            <Route path="/videos">
                              <Route index element={<VideoFolders />} />
                              <Route path=":folderId" element={<VideoList />} />
                            </Route>
                            <Route path="/subscribers" element={<Subscribers />} />
                            <Route path="/admin-users" element={<AdminUsersPage />} />
                            <Route path="/admin-users/:userId" element={<UserDetails />} />
                            <Route path="/member-countries" element={<MemberCountries />} />
                            <Route path="/member-countries/:regionId" element={<MemberCountryRegion />} />
                            <Route path="/member-countries/:regionId/:memberCountryId" element={<MemberCountryDetailPage />} />
                            <Route path="/member-countries/:regionId/add" element={<MemberCountryEditor />} />
                            <Route path="/member-countries/:regionId/edit/:memberCountryId" element={<MemberCountryEditor />} />
                            <Route path="/members" element={<Members />} />
                            <Route path="/members/:memberId" element={<MemberDetailPage />} />
                            <Route path="/members/add" element={<MemberEditor />} />
                            <Route path="/members/edit/:memberId" element={<MemberEditor />} />
                            <Route path="/history" element={<History />} />
                            <Route path="/history/:historyId" element={<HistoryDetailPage />} />
                            <Route path="/history/add" element={<HistoryEditor />} />
                            <Route path="/history/edit/:historyId" element={<HistoryEditor />} />
                            <Route path="/documents" element={<Documents />} />
                            <Route path="/documents/:documentId" element={<DocumentDetailPage />} />
                            <Route path="/documents/add" element={<DocumentEditor />} />
                            <Route path="/documents/edit/:documentId" element={<DocumentEditor />} />
                            <Route path="/jobs">
                              <Route index element={<Jobs />} />
                              <Route path="add" element={<JobEditor />} />
                              <Route path=":id" element={<JobDetailPage />} />
                              <Route path="edit/:jobId" element={<JobEditor />} />
                            </Route>
                            <Route path="/applications">
                              <Route index element={<Applications />} />
                              <Route path="add" element={<ApplicationEditor />} />
                              <Route path=":id" element={<ApplicationDetailPage />} />
                              <Route path="edit/:applicationId" element={<ApplicationEditor />} />
                            </Route>
                            <Route path="/tenders">
                              <Route index element={<Tenders />} />
                              <Route path="add" element={<TenderEditor />} />
                              <Route path=":id" element={<TenderDetailPage />} />
                              <Route path="edit/:tenderId" element={<TenderEditor />} />
                            </Route>
                            <Route path="/events">
                              <Route index element={<Events />} />
                              <Route path="add" element={<EventEditor />} />
                              <Route path=":id" element={<EventDetailPage />} />
                              <Route path="edit/:eventId" element={<EventEditor />} />
                            </Route>
                            <Route path="/announcement" element={<Announcements />} />
                            <Route path="/my-profile" element={<MyProfile />} />
                          </Routes>
                        </AdminLayout>
                      </ViewProvider>
                    </AuthGuard>
                  }
                />

                <Route path="*" element={<Navigate to="/login" replace />} />
              </Routes>
            </MicroSiteProvider>
          </PublishSuccessProvider>
        </Router>
      </ModalProvider>
    </QueryProvider>
      </div>
    </>
  )
}

export default AdminApp